@file:OptIn(ExperimentalTime::class, ExperimentalUuidApi::class)

package dev.moetz.sslcertificatetool.page

import dev.moetz.materialize.MaterializeTab
import dev.moetz.materialize.mCard
import dev.moetz.materialize.mCol
import dev.moetz.materialize.mContainer
import dev.moetz.materialize.mHeader
import dev.moetz.materialize.mLoadingCircle
import dev.moetz.materialize.mRow
import dev.moetz.sslcertificatetool.design.mFooter
import dev.moetz.sslcertificatetool.model.CertificateInformation
import dev.moetz.sslcertificatetool.util.WebsocketClientServiceManager
import dev.moetz.sslcertificatetool.util.useStateFlow
import emotion.react.css
import io.ktor.util.encodeBase64
import kotlinx.serialization.builtins.nullable
import react.FC
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.br
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.h4
import react.dom.html.ReactHTML.h5
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.pre
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.ul
import web.cssom.ClassName
import web.cssom.Color
import web.window.WindowTarget
import web.window._blank
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

external interface DomainPageServiceProps : Props {
    var manager: DomainPageServiceManager
}

class DomainPageServiceManager(
    val id: Uuid,
    val activeTab: DomainPageTab,
) : WebsocketClientServiceManager<CertificateInformation?>(
    websocketPath = "/api/ws/domain/$id",
    initialState = null,
    stateSerializer = CertificateInformation.serializer().nullable,
    localStorageConfig = LocalStorageConfig(key = "websocket_certificate_information_service_manager_state_$id"),
) {

}

enum class DomainPageTab(val pathName: String) {
    General(""),
    OpenSSL("openssl"),
    SCT("sct");

    fun getPath(id: Uuid): String = buildString {
        append("/domain/$id")
        if (pathName.isNotBlank()) {
            append("/$pathName")
        }
    }
}


fun String.parseHexColonStringToByteArray(): ByteArray {
    return this.split(":")
        .map { it.trim().toInt(16).toByte() }
        .toByteArray()
}


val DomainPageService = FC<DomainPageServiceProps> { props ->
    val certificateInformation = useStateFlow(props.manager.stateAsStateFlow)

    mHeader(
        title = "Certificate Information",
        tabs = DomainPageTab.entries.map { tab ->
            MaterializeTab(
                label = tab.name,
                active = tab == props.manager.activeTab,
                href = props.manager.getFullUrl(tab.getPath(props.manager.id)),
            )
        },
    )

    mContainer {
        if (certificateInformation != null) {
            mRow {
                mCol(small = 12) {
                    h3 {
                        a {
                            href = "https://${certificateInformation.domain}"
                            target = WindowTarget._blank
                            +certificateInformation.domain
                        }
                    }
                }
                mCol(small = 12, additionalClass = "right-align") {
                    +"Checked at ${certificateInformation.checkedAt}"
                }
            }

            when (props.manager.activeTab) {
                DomainPageTab.General -> {
                    mRow {
                        mCol(small = 12) {
                            h4 { +"Certificates in chain:" }
                        }
                        certificateInformation.certificates.forEachIndexed { index, certificate ->
                            mCol(small = 12) {
                                mCard(
                                    title = certificate.subject,
                                    content = {
                                        if (certificate.subjectAlternatives.isNotEmpty()) {
                                            mRow {
                                                mCol(small = 12) {
                                                    +"Alternative names:"
                                                    ul {
                                                        certificate.subjectAlternatives.forEach {
                                                            li { +it }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        mRow {
                                            mCol(small = 12) {
                                                +"Not before: "
                                                span {
                                                    css {
                                                        color = if (certificate.notBefore < Clock.System.now()) {
                                                            Color("#c6ffc6")
                                                        } else {
                                                            Color("red")
                                                        }
                                                    }
                                                    +certificate.notBefore.toString()
                                                }
                                            }
                                            mCol(small = 12) {
                                                +"Not after: "
                                                span {
                                                    css {
                                                        color = if (certificate.notAfter > Clock.System.now()) {
                                                            Color("#c6ffc6")
                                                        } else {
                                                            Color("red")
                                                        }
                                                    }
                                                    +certificate.notAfter.toString()
                                                }
                                            }
                                        }

                                        if (index == 0) {
                                            mRow {
                                                mCol(small = 12) {
                                                    h5 { +"SCTs" }
                                                }
                                                certificateInformation.scts.forEach { sct ->
                                                    if (sct.logList != null) {
                                                        mCol(small = 12) {
                                                            +sct.logList.nameAndOperator
                                                            if (sct.logList.isTiled) {
                                                                span {
                                                                    className = ClassName("orange-text")
                                                                    +" (warning: tiled!)"
                                                                }
                                                            }
                                                        }
                                                    } else {
                                                        mCol(small = 12, additionalClass = "red-text") {
                                                            +"${sct.logId} not found in log list."
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    },
                                )
                            }
                        }
                    }
                }

                DomainPageTab.SCT -> {
                    mRow {
                        mCol(small = 12) {
                            h4 { +"SCTs in leaf certificate:" }
                        }
                        certificateInformation.scts.forEach { sct ->
                            mCol(small = 12) {
                                mCard(
                                    title = sct.logList?.nameAndOperator ?: sct.logId,
                                    cardColor = "blue-grey darken-4",
                                    content = {
                                        mRow {
                                            mCol(small = 12) {
                                                +"Version: ${sct.version}"
                                            }
                                            mCol(small = 12) {
                                                +"Log ID (hex): ${sct.logId}"
                                                br()
                                                +"Log ID (base64): ${sct.logId.parseHexColonStringToByteArray().encodeBase64()}"
                                            }
                                            mCol(small = 12) {
                                                +"Timestamp: ${sct.timestamp}"
                                            }
                                            mCol(small = 12) {
                                                +"Extensions: ${sct.extension ?: "None"}"
                                            }
                                            mCol(small = 12) {
                                                val split = sct.signature.split(": ", limit = 3)
                                                val algorithm = split.getOrNull(1)
                                                val signature = split.getOrNull(2)

                                                +"Signature:"
                                                br()
                                                +"$algorithm"
                                                pre {
                                                    signature.orEmpty()
                                                        .split(":")
                                                        .batch(16)
                                                        .forEachWithIsLast { line, isLastLine ->
                                                            line.forEachWithIsLast { hex, isLastHex ->
                                                                +hex
                                                                if (!isLastLine || !isLastHex) {
                                                                    +":"
                                                                }
                                                            }
                                                            br()
                                                        }
                                                }
                                            }
                                            if (sct.logList != null) {
                                                mCol(small = 12) {
                                                    a {
                                                        css {
                                                            color = Color("#3cff7b")
                                                        }
                                                        +"Found in log list: ${sct.logList.nameAndOperator}"
                                                        href = sct.logList.url
                                                        target = WindowTarget._blank
                                                    }
                                                    if (sct.logList.isTiled) {
                                                        span {
                                                            className = ClassName("red-text")
                                                            +" (warning: tiled log list!)"
                                                        }
                                                    }
                                                }
                                            } else {
                                                mCol(small = 12, additionalClass = "red-text") {
                                                    b { +"Not found in any CT logs!" }
                                                }
                                            }
                                        }
                                    },
                                )
                            }
                        }
                    }
                }

                DomainPageTab.OpenSSL -> {
                    mRow {
                        mCol(small = 12, additionalClass = "left-align") {
                            pre {
                                +certificateInformation.opensslInformation.fullOutput
                            }
                        }
                    }
                }
            }
        } else {
            mRow {
                mCol(small = 12) {
                    mLoadingCircle()
                }
            }
        }
    }

    mFooter()
}


fun <T> List<T>.batch(batchSize: Int): List<List<T>> {
    val result = mutableListOf<List<T>>()
    val currentList = mutableListOf<T>()
    var index = 0

    this.forEach { item ->
        currentList.add(item)
        index++
        if (index >= batchSize) {
            index = 0
            result.add(currentList.toList())
            currentList.clear()
        }
    }
    result.add(currentList.toList())

    return result.toList()
}

fun <T> List<T>.forEachWithIsLast(block: (item: T, isLast: Boolean) -> Unit) {
    this.forEachIndexed { index, item ->
        block.invoke(item, index == this.lastIndex)
    }
}